import { Breadcrumbs } from "@amzn/stencil-react-components/breadcrumbs";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import React, { FC, useState } from "react";
import { useOpenOrders } from "@/hooks/use-open-orders";
import { useHardwareSupportFaq } from "@/hooks/use-hardware-support-faq";
import { usePostComment } from "@/hooks/use-post-comment";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { UserPermissions } from "@/services/user-permissions/user-permissions";

interface HardwareOrderSupportProps {
  onBackClick: () => void;
  onSendMessageClick: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const NOT_LISTED = "not-listed";

export const HardwareOrderSupport: FC<HardwareOrderSupportProps> = ({ onBackClick, onSendMessageClick }) => {
  const [selectedOrder, setSelectedOrder] = useState<string | undefined>(undefined);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [isCustomIssue, setIsCustomIssue] = useState(false);
  const [disableIssueSelect, setDisableIssueSelect] = useState(true);
  const [message, setMessage] = useState("");

  const { data: orders = [] } = useOpenOrders();
  const { data: faqs = [] } = useHardwareSupportFaq();
  const { mutateAsync: postComment, isPending, error: postError } = usePostComment();
  const username = UserPermissions.current.username || "";

  const selectedFaq = faqs.find((faq) => faq.id === selectedQuestion);

  const replacePlaceholders = (text: string): string => {
    return text
      .replace(/\{\{location\}\}/g, username)
      .replace(/\{\{date\}\}/g, new Date().toLocaleDateString());
  };

  const orderOptions: SelectOption[] = orders.map((order) => ({
    value: order.orderId,
    label: order.items?.[0]?.itemInformation?.name ?? "Unknown item",
  }));

  const issueOptions: SelectOption[] = faqs.map((faq) => ({
    value: faq.id,
    label: faq.question,
  }));

  const handleOrderChange = (option: SelectOption) => {
    setSelectedOrder(option.value);
    setDisableIssueSelect(false);
  };

  const handleQuestionChange = (option: SelectOption) => {
    setSelectedQuestion(option.value);
    setIsCustomIssue(option.value === NOT_LISTED);
  };

  const sendComment = async () => {
    if (!selectedOrder) return;
    await postComment({ taxonomyId: selectedOrder, message });
    onSendMessageClick();
    setMessage("");
  };

  const renderOrderOption = (option: SelectOption) => {
    const order = orders.find((o) => o.orderId === option.value);
    return (
      <Row gridGap="S200" alignItems="center">
        <Text fontWeight="bold">{option.label}</Text>
        <Text>({getString("hardwareOrderSupport.orderNumberDisplay")}{order?.orderReference})</Text>
      </Row>
    );
  };

  return (
    <Col>
      {postError && (
        <Row>
          <MessageBanner
            data-testid="hw-support-error-banner"
            dismissButtonAltText="Dismiss"
            hasRoundedCorners={true}
            type={MessageBannerType.Error}
            isDismissible={true}
          >
            Something went wrong. If the error persists,{" "}
            <Link target="_blank" href={getString("externalLinks.shopErrorTicketTemplate")}>
              contact support
            </Link>.
          </MessageBanner>
        </Row>
      )}

      <Row>
        <Breadcrumbs
          onCrumbClick={(e) => { e.preventDefault(); onBackClick(); }}
          crumbs={[{ title: "Get hardware support", url: "/" }, { title: "Get order support" }]}
        />
      </Row>

      <Row>
        <Text>{getString("hardwareOrderSupport.supportText")}</Text>
      </Row>

      <Spacer height="S500" />

      <Row>
        <Col width="100%" gridGap="S200">
          <Text fontWeight="medium">{getString("hardwareOrderSupport.orderSelectorTitle")}</Text>
          <Select
            data-testid="select-order"
            onChange={handleOrderChange}
            placeholder={getString("hardwareOrderSupport.orderSelectPlaceholder")}
            options={orderOptions}
            renderOption={renderOrderOption}
          />
        </Col>
      </Row>

      <Spacer height="S500" />

      <Row>
        <Col width="100%" gridGap="S200">
          <Text fontWeight="medium">{getString("hardwareOrderSupport.issueSelectorTitle")}</Text>
          <Select
            data-testid="select-issue"
            onChange={handleQuestionChange}
            placeholder={getString("hardwareOrderSupport.issueSelectPlaceholder")}
            options={issueOptions}
            disabled={disableIssueSelect}
            renderOption={(option: SelectOption) => <Text>{option.label}</Text>}
          />

          {!isCustomIssue && selectedFaq?.answer && (
            <Text data-testid="faq-answer">{replacePlaceholders(selectedFaq.answer)}</Text>
          )}

          {isCustomIssue && (
            <Col>
              <Spacer height="S200" />
              <Row alignItems="center" gridGap="S200">
                <img
                  src={`https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${username}`}
                  alt="You"
                  style={{ width: 32, height: 32, borderRadius: "50%" }}
                />
                <Text fontSize="T200" fontWeight="bold">You</Text>
              </Row>
              <Spacer height="S200" />
              <TextArea
                data-testid="message-textarea"
                width="100%"
                placeholder={getString("hardwareOrderSupport.messagePlaceholder")}
                value={message}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
              />
              <Row justifyContent="flex-end" padding="S200">
                <Button
                  data-testid="submit-button"
                  variant={ButtonVariant.Primary}
                  onClick={() => sendComment()}
                  disabled={!message.trim() || isPending}
                >
                  {getString("hardwareOrderSupport.submitButton")}
                  {isPending && <Spinner />}
                </Button>
              </Row>
            </Col>
          )}
        </Col>
      </Row>
    </Col>
  );
};
